import React, { useState, useRef, useEffect } from 'react';
import { Clock, MoreVertical, Check, AlertCircle, XCircle, CheckCircle } from 'lucide-react';
import { t } from 'i18next';

interface AppointmentProps {
  appointment: {
    id: string;
    time: string;
    clientName: string;
    service: string;
    duration: string;
    status: 'confirmed' | 'cancelled' | 'completed';
    totalPrice: number
  };
  onStatusChange?: (id: string, newStatus: 'confirmed' | 'cancelled' | 'completed') => void;
}

const ReservationDashboardCard: React.FC<AppointmentProps> = ({ appointment, onStatusChange }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  
  // Fermer le menu si on clique ailleurs sur la page
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  const handleStatusChange = (newStatus: 'confirmed' | 'cancelled' | 'completed') => {
    if (onStatusChange) {
      onStatusChange(appointment.id, newStatus);
    }
    setMenuOpen(false);
  };
  const getAppointmentDate = (timeStr: string) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  };
  
  // Séparer les heures et les minutes pour l'affichage
  const getTimeComponents = (timeStr: string) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return {
      hours: hours.toString().padStart(2, '0'),
      minutes: minutes.toString().padStart(2, '0')
    };
  };

  const now = new Date();
  const appointmentTime = getAppointmentDate(appointment.time);
  const [durationMinutes] = appointment.duration.split(' ')[0].split(':').map(Number) || [0];
  const endTime = new Date(appointmentTime);
  endTime.setMinutes(endTime.getMinutes() + durationMinutes);

  const isPast = now > endTime;




  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'confirmed':
        return {
          icon: <Check size={12} />,
          color: 'bg-indigo-100 tex-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400',
          label: 'Confirmé'
        };
      case 'completed':
        return {
          icon: <CheckCircle size={12} />,
          color: 'bg-gray-100 text-gray-600 dark:bg-gray-900/20 dark:text-gray-400',
          label: 'Terminé'
        };
      case 'cancelled':
        return {
          icon: <XCircle size={12} />,
          color: 'bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400',
          label: 'Annulé'
        };
      default:
        return {
          icon: null,
          color: 'bg-primary/10 text-primary dark:bg-primary-dark/20 dark:text-primary-light',
          label: status
        };
    }
  };

  const statusInfo = getStatusInfo(appointment.status);


  return (
    <div 
      className={`
        relative p-4 rounded-xl shadow-sm transition-all duration-300
        border-l-4 ${appointment.status === 'confirmed' ? 'border-l-primary' : appointment.status === 'completed' ? 'border-l-gray-500' : 'border-l-red-500'}
        bg-white dark:bg-gray-800 hover:shadow-md
        ${appointment.status === 'cancelled' || appointment.status === "completed" ? 'opacity-70' : ''}
      `}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <div className="flex flex-col items-center justify-center w-12 h-12 bg-primary/10 dark:bg-primary-dark/20 rounded-full">
            <div className="font-medium text-sm leading-none text-primary dark:text-primary-light">
              {getTimeComponents(appointment.time).hours}
            </div>
            <div className="text-xs leading-tight text-primary/70 dark:text-primary-light/70 mt-0.5">
              {getTimeComponents(appointment.time).minutes}
            </div>
          </div>
          <div>
            <h3 className="text-sm font-medium text-text-primary dark:text-white">
              {appointment.clientName}
            </h3>
            <div className="flex items-center gap-1 mt-0.5">
              <p className="text-xs text-text-secondary dark:text-gray-400">{appointment.service}</p>
              <span className="inline-block w-1 h-1 rounded-full bg-gray-300 dark:bg-gray-600"></span>
              <div className="flex items-center text-text-secondary dark:text-gray-400 text-xs">
                <Clock size={10} className="mr-0.5"/>
                {appointment.duration}
              </div>
              <span className="inline-block w-1 h-1 rounded-full bg-gray-300 dark:bg-gray-600"></span>
              <div className="flex items-center text-text-secondary dark:text-gray-400 text-xs font-medium">
                {appointment.totalPrice}€
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className={`flex items-center px-2 py-0.5 rounded-full text-xs ${statusInfo.color}`}>
            {statusInfo.icon}
            <span className="ml-1">{statusInfo.label}</span>
          </div>

          {appointment.status !== "cancelled" && <div className="relative" ref={menuRef}>
            <button
                className="p-1 rounded-full hover:bg-background-dark/5 dark:hover:bg-gray-700 text-text-secondary dark:text-gray-400"
                onClick={() => setMenuOpen(!menuOpen)}
            >
              <MoreVertical size={14}/>
            </button>

            {menuOpen && (
                <div
                    className="absolute right-0 mt-1 w-48 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-10">
                  <div className="py-1" role="menu" aria-orientation="vertical">
                    <button
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => handleStatusChange('cancelled')}
                        role="menuitem"
                    >
                      <XCircle size={16} className="mr-2 text-red-500"/>
                      {t('dashboard.cancelAppointment', 'Marquer comme annulé')}
                    </button>
                  </div>
                </div>
            )}
          </div>}
        </div>
      </div>
    </div>
  );
};

export default ReservationDashboardCard;